import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Dialog, Box, Typography, TextField, Button, IconButton,
  FormControl, Select, MenuItem, ListSubheader, CircularProgress,
} from "@mui/material";
import {
  Close as IconClose,
  Send as IconSend,
  Sms as SmsIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { formatPhoneNumber } from "../../utils/phoneFormat";
import { getAllVoiceProfiles, ensureStarterModes, getCompanyKnowledge } from "../../modules/mymagic/utils/voiceProfileDB";
import { useWebLLM } from "../../contexts/WebLLMContext";
import { generateSmsWithWebLLM } from "../../utils/assembleSmsPrompt";
import { apiSendSms } from "../../graphQL/twilio";

const SMS_CHAR_LIMIT = 160;

function getPrimaryPhone(contact) {
  if (!contact) return "";
  if (contact.phone) return String(contact.phone);
  const reach = contact?.reach || {};
  const phones = Array.isArray(reach.phones) ? reach.phones : [];
  const primary = phones.find((p) => p?.isPrimary && p?.value) || phones.find((p) => p?.value);
  return primary?.value ? String(primary.value) : "";
}

function getDisplayName(contact) {
  if (!contact) return "";
  const identity = contact.identity || {};
  const person = identity.person || {};
  const first = (person.firstName || contact.firstName || "").trim();
  const last = (person.lastName || contact.lastName || "").trim();
  const full = `${first} ${last}`.trim();
  return full || identity.company?.name || contact.company || "Recipient";
}

export default function ComposeTextDialog({ open, onClose, contact, onSend }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState("");
  const [modes, setModes] = useState([]);
  const [companyKnowledge, setCompanyKnowledge] = useState("");
  const [writingModeId, setWritingModeId] = useState("");
  const [generating, setGenerating] = useState(false);

  const { engine, ready: llmReady, downloading, progress, init: initLLM } = useWebLLM();
  const reduxUser = useSelector((state) => state?.auth?.user);
  const senderFirstName = reduxUser?.firstName || reduxUser?.name?.split(" ")[0] || "";

  const phone = getPrimaryPhone(contact);
  const displayName = getDisplayName(contact);
  const remaining = SMS_CHAR_LIMIT - body.length;
  const overLimit = remaining < 0;

  useEffect(() => {
    if (open) {
      setBody("");
      setStatus("");
      setWritingModeId("");
      initLLM();
    }
  }, [open, contact?.id, initLLM]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      await ensureStarterModes("text");
      const [all, ck] = await Promise.all([
        getAllVoiceProfiles("text"),
        getCompanyKnowledge(),
      ]);
      if (!cancelled) {
        setModes(all || []);
        setCompanyKnowledge(typeof ck === "string" ? ck : "");
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const selectedMode = modes.find((m) => m.id === writingModeId) || null;

  const handleGenerate = useCallback(async () => {
    if (!selectedMode || !engine || generating) return;
    setGenerating(true);
    setStatus("");
    const seed = body.trim();
    setBody("");
    try {
      const modePurpose =
        seed
        || selectedMode?.defaultPrompt
        || selectedMode?.customInstructions
        || `Write an SMS in ${selectedMode?.name || "the selected"} tone with a clear next step.`;
      await generateSmsWithWebLLM(
        engine,
        {
          voiceProfile: selectedMode,
          contact,
          purpose: modePurpose,
          companyKnowledge,
          senderFirstName,
        },
        (_delta, accumulated) => setBody(accumulated),
      );
    } catch (err) {
      console.error("SMS generation failed:", err);
      setStatus(`Failed: ${err?.message || "generation error"}`);
    } finally {
      setGenerating(false);
    }
  }, [selectedMode, engine, generating, body, contact, companyKnowledge, senderFirstName]);

  const handleSend = async () => {
    if (!phone || !body.trim() || sending) return;
    setSending(true);
    setStatus("");
    try {
      if (onSend) {
        const tone = modes.find((m) => m.id === writingModeId) || null;
        await onSend({ contact, phone, body, tone });
      } else {
        const out = await apiSendSms({ toPhone: phone, body });
        if (!out || out.status !== "success") {
          throw new Error(out?.error || "SMS send failed");
        }
      }
      setStatus("Sent.");
      setTimeout(() => onClose?.(), 800);
    } catch (err) {
      console.error("SMS send failed:", err);
      setStatus(`Failed: ${err?.message || "unknown error"}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1, borderBottom: "1px solid #E5E7EB", backgroundColor: "#F9FAFB", gap: 1 }}>
        <SmsIcon sx={{ fontSize: 18, color: "#6B7280" }} />
        <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 700, color: "#111827" }}>
          Compose Text
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <IconClose sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", minWidth: 36 }}>To</Typography>
          <Box sx={{ flex: 1, fontSize: 13, color: phone ? "#0F172A" : "#DC2626" }}>
            {phone ? (
              <>
                {displayName} &lt;{formatPhoneNumber(phone)}&gt;
              </>
            ) : (
              "No phone number on file"
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", minWidth: 36 }}>Tone</Typography>
          <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
            <Select
              value={writingModeId}
              onChange={(e) => setWritingModeId(e.target.value)}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return <span style={{ color: "#9CA3AF" }}>Select a tone</span>;
                const mode = modes.find((m) => m.id === selected);
                return mode?.name || selected;
              }}
              sx={{ backgroundColor: "#fff", fontSize: 13, "& .MuiSelect-select": { py: 0.75 } }}
            >
              {(() => {
                const customModes = modes.filter((m) => !m.isStarter);
                const starterModes = modes.filter((m) => m.isStarter);
                const items = [];
                if (customModes.length > 0) {
                  items.push(
                    <ListSubheader key="hdr-custom" sx={{ fontSize: 11, fontWeight: 700, color: "#7c3aed", lineHeight: "28px" }}>
                      My Modes
                    </ListSubheader>
                  );
                  customModes.forEach((m) =>
                    items.push(
                      <MenuItem key={m.id} value={m.id} sx={{ fontSize: 13 }}>{m.name}</MenuItem>
                    )
                  );
                }
                if (starterModes.length > 0 && customModes.length > 0) {
                  items.push(
                    <ListSubheader key="hdr-starter" sx={{ fontSize: 11, fontWeight: 700, color: "#64748B", lineHeight: "28px" }}>
                      Starter Modes
                    </ListSubheader>
                  );
                }
                starterModes.forEach((m) =>
                  items.push(
                    <MenuItem key={m.id} value={m.id} sx={{ fontSize: 13 }}>{m.name}</MenuItem>
                  )
                );
                return items;
              })()}
            </Select>
          </FormControl>
          <Button
            size="small"
            variant="contained"
            startIcon={
              generating || downloading
                ? <CircularProgress size={12} sx={{ color: "#FFFFFF" }} />
                : <AutoAwesomeIcon sx={{ fontSize: 14 }} />
            }
            onClick={handleGenerate}
            disabled={!selectedMode || !llmReady || generating}
            title={
              !selectedMode
                ? "Pick a tone first"
                : downloading
                  ? `Downloading model… ${Math.round((progress || 0) * 100)}%`
                  : !llmReady
                    ? "Local model loading…"
                    : "Generate text with the selected tone"
            }
            sx={{
              textTransform: "none",
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 999,
              px: 1.5,
              py: 0.5,
              whiteSpace: "nowrap",
              flexShrink: 0,
              backgroundColor: "#7C3AED",
              boxShadow: "0 4px 10px rgba(124,58,237,0.25)",
              "&:hover": { backgroundColor: "#6D28D9" },
              "&.Mui-disabled": { backgroundColor: "#E5E7EB", color: "#9CA3AF" },
            }}
          >
            {generating
              ? "Generating…"
              : downloading
                ? `${Math.round((progress || 0) * 100)}%`
                : "Draft with Magic"}
          </Button>
        </Box>

        <TextField
          multiline
          minRows={5}
          maxRows={10}
          fullWidth
          placeholder="Type your message…"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={!phone}
          sx={{ "& .MuiOutlinedInput-root": { fontSize: 13, lineHeight: 1.5 } }}
        />

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 11, color: overLimit ? "#DC2626" : "#64748B" }}>
            {remaining} / {SMS_CHAR_LIMIT} chars
            {overLimit && " — will send as multi-part"}
          </Typography>
          {status && (
            <Typography sx={{ fontSize: 11, color: status.startsWith("Failed") ? "#DC2626" : "#16A34A" }}>
              {status}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 0.5 }}>
          <Button
            size="small"
            variant="contained"
            startIcon={<IconSend sx={{ fontSize: 14 }} />}
            onClick={handleSend}
            disabled={!phone || !body.trim() || sending}
            sx={{
              textTransform: "none", fontSize: 12, fontWeight: 600,
              backgroundColor: "#16A34A", "&:hover": { backgroundColor: "#15803D" },
            }}
          >
            {sending ? "Sending…" : "Send"}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
